/**
 * Undo/Redo hook for Todo Lists (W6 — split-cache shape).
 *
 * Snapshots the (sections, items) tuple before each mutation. On
 * undo/redo, restores the cache optimistically and syncs the server
 * via a field-level diff:
 *
 *   - Item present in TARGET but not in CURRENT → re-create
 *     (UNSUPPORTED today — re-creating yields a new itemId, so any
 *     references to the old id would dangle. We surface a toast and
 *     skip. The most common undo cases — accidental toggle, edit,
 *     create — do not hit this branch.)
 *   - Item present in CURRENT but not in TARGET → delete
 *     (undoes a create cleanly: the create lives in CURRENT, the
 *     pre-create snapshot doesn't have it, so undo removes it.)
 *   - Item in both with field differences → updateItem(diff)
 *
 * Sections work the same way against `updateTodoSection`.
 *
 * The legacy implementation snapshotted a single combined cache slice;
 * this rewrite handles the W6 split (`sections(holder, list)` +
 * `items(holder, list)`) by snapshotting both and restoring atomically.
 */

import { useCallback, useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useToast } from '@/components/ui/use-toast';
import { queryKeys } from '@/lib/query-keys';
import api from '@/lib/api';
import type { TodoItemResponse, TodoSectionResponse } from '@/lib/api';
import { useUndoRedo } from '@/lib/hooks/useUndoRedo';

interface TodoListSnapshot {
  sections: TodoSectionResponse[];
  items: TodoItemResponse[];
}

interface UseTodoUndoRedoOptions {
  holderId: string | null;
  listId: string;
  enabled?: boolean;
}

interface UseTodoUndoRedoReturn {
  canUndo: boolean;
  canRedo: boolean;
  undo: () => void;
  redo: () => void;
  captureSnapshot: () => void;
  clear: () => void;
}

// ---------------------------------------------------------------------------
// Per-field diff helpers
// ---------------------------------------------------------------------------

function diffItem(
  current: TodoItemResponse,
  target: TodoItemResponse,
): Partial<TodoItemResponse> | null {
  const out: Partial<TodoItemResponse> = {};
  if (current.title !== target.title) out.title = target.title;
  if (current.description !== target.description) out.description = target.description;
  if (current.status !== target.status) out.status = target.status;
  if (current.sectionId !== target.sectionId) out.sectionId = target.sectionId;
  if (current.sortOrder !== target.sortOrder) out.sortOrder = target.sortOrder;
  return Object.keys(out).length === 0 ? null : out;
}

function diffSection(
  current: TodoSectionResponse,
  target: TodoSectionResponse,
): Partial<TodoSectionResponse> | null {
  const out: Partial<TodoSectionResponse> = {};
  if (current.name !== target.name) out.name = target.name;
  if (current.description !== target.description) out.description = target.description;
  if (current.sortOrder !== target.sortOrder) out.sortOrder = target.sortOrder;
  if (current.sectionType !== target.sectionType) out.sectionType = target.sectionType;
  if (current.parentSectionId !== target.parentSectionId)
    out.parentSectionId = target.parentSectionId;
  if (current.hiddenFromPublic !== target.hiddenFromPublic)
    out.hiddenFromPublic = target.hiddenFromPublic;
  return Object.keys(out).length === 0 ? null : out;
}

// ---------------------------------------------------------------------------
// Server sync
// ---------------------------------------------------------------------------

interface SyncResult {
  unsupportedRecreates: number;
}

async function syncItemsToServer(
  holderId: string,
  current: TodoItemResponse[],
  target: TodoItemResponse[],
): Promise<SyncResult> {
  const currentById = new Map(current.map((i) => [i.itemId, i]));
  const targetById = new Map(target.map((i) => [i.itemId, i]));

  // Deletions: in current but not in target → delete on server (undoes a recent create)
  for (const item of current) {
    if (!targetById.has(item.itemId)) {
      await api.deleteTodoItem(holderId, item.itemId);
    }
  }

  // Updates: in both → patch the diff
  for (const t of target) {
    const c = currentById.get(t.itemId);
    if (!c) continue;
    const diff = diffItem(c, t);
    if (diff) {
      await api.updateTodoItem(holderId, t.itemId, {
        title: diff.title,
        description: diff.description,
        status: diff.status,
        sectionId: diff.sectionId,
        sortOrder: diff.sortOrder,
      });
    }
  }

  // Re-creates: in target but not in current. Not supported — the
  // create call would mint a new itemId, breaking any references to
  // the original id. Count + surface via toast.
  let unsupportedRecreates = 0;
  for (const t of target) {
    if (!currentById.has(t.itemId)) unsupportedRecreates += 1;
  }
  return { unsupportedRecreates };
}

async function syncSectionsToServer(
  holderId: string,
  current: TodoSectionResponse[],
  target: TodoSectionResponse[],
): Promise<SyncResult> {
  const currentById = new Map(current.map((s) => [s.sectionId, s]));
  const targetById = new Map(target.map((s) => [s.sectionId, s]));

  for (const section of current) {
    if (!targetById.has(section.sectionId)) {
      await api.deleteTodoSection(holderId, section.sectionId);
    }
  }
  for (const t of target) {
    const c = currentById.get(t.sectionId);
    if (!c) continue;
    const diff = diffSection(c, t);
    if (diff) {
      await api.updateTodoSection(holderId, t.sectionId, diff);
    }
  }
  let unsupportedRecreates = 0;
  for (const t of target) {
    if (!currentById.has(t.sectionId)) unsupportedRecreates += 1;
  }
  return { unsupportedRecreates };
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export function useTodoUndoRedo({
  holderId,
  listId,
  enabled = true,
}: UseTodoUndoRedoOptions): UseTodoUndoRedoReturn {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const sectionsKey = queryKeys.todoLists.sections(holderId ?? '', listId);
  const itemsKey = queryKeys.todoLists.items(holderId ?? '', listId);

  const {
    canUndo,
    canRedo,
    snapshot,
    undo: undoRaw,
    redo: redoRaw,
    clear,
  } = useUndoRedo<TodoListSnapshot>({ maxHistory: 50 });

  // Track whether a mutation is pending so we know to snapshot the
  // post-mutation state when it arrives in the cache.
  const pendingMutation = useRef(false);
  // Suppress cache subscription while we're optimistically restoring
  // during undo/redo so we don't snapshot the restored value back onto
  // the stack.
  const isApplying = useRef(false);
  const initialCaptured = useRef(false);

  const readSnapshot = useCallback((): TodoListSnapshot => {
    const sections =
      queryClient.getQueryData<{ sections: TodoSectionResponse[] }>(sectionsKey)?.sections ?? [];
    const items = queryClient.getQueryData<{ items: TodoItemResponse[] }>(itemsKey)?.items ?? [];
    return { sections, items };
  }, [queryClient, sectionsKey, itemsKey]);

  // Subscribe to the items cache to capture post-mutation snapshots.
  // (Snapshotting on items is sufficient — section-only changes are
  // rare and the next item mutation will catch them.)
  useEffect(() => {
    if (!enabled || !holderId) return;
    const unsub = queryClient.getQueryCache().subscribe((event) => {
      if (isApplying.current) return;
      if (event.type !== 'updated' || event.action.type !== 'success') return;
      const eventKey = JSON.stringify(event.query.queryKey);
      const myItemsKey = JSON.stringify(itemsKey);
      const mySectionsKey = JSON.stringify(sectionsKey);
      if (eventKey !== myItemsKey && eventKey !== mySectionsKey) return;

      const snap = readSnapshot();
      if (!initialCaptured.current) {
        snapshot(snap);
        initialCaptured.current = true;
        return;
      }
      if (pendingMutation.current) {
        pendingMutation.current = false;
        snapshot(snap);
      }
    });
    return unsub;
  }, [enabled, holderId, queryClient, itemsKey, sectionsKey, readSnapshot, snapshot]);

  const captureSnapshot = useCallback(() => {
    if (!enabled || !holderId) return;
    snapshot(readSnapshot());
    pendingMutation.current = true;
  }, [enabled, holderId, snapshot, readSnapshot]);

  const applyState = useCallback(
    async (target: TodoListSnapshot | null) => {
      if (!target || !holderId) return;
      const current = readSnapshot();

      isApplying.current = true;
      await queryClient.cancelQueries({ queryKey: sectionsKey });
      await queryClient.cancelQueries({ queryKey: itemsKey });
      queryClient.setQueryData(sectionsKey, { sections: target.sections });
      queryClient.setQueryData(itemsKey, { items: target.items });
      isApplying.current = false;

      try {
        const itemResult = await syncItemsToServer(holderId, current.items, target.items);
        const sectionResult = await syncSectionsToServer(
          holderId,
          current.sections,
          target.sections,
        );
        const totalUnsupported =
          itemResult.unsupportedRecreates + sectionResult.unsupportedRecreates;
        if (totalUnsupported > 0) {
          toast({
            title: 'Undo partial',
            description:
              totalUnsupported === 1
                ? '1 deleted row cannot be restored. Re-create it manually if needed.'
                : `${totalUnsupported} deleted rows cannot be restored. Re-create them manually if needed.`,
            variant: 'destructive',
          });
        }
      } finally {
        queryClient.invalidateQueries({ queryKey: sectionsKey });
        queryClient.invalidateQueries({ queryKey: itemsKey });
      }
    },
    [holderId, readSnapshot, queryClient, sectionsKey, itemsKey, toast],
  );

  const undo = useCallback(() => {
    if (!enabled || !holderId) return;
    const state = undoRaw();
    applyState(state);
  }, [enabled, holderId, undoRaw, applyState]);

  const redo = useCallback(() => {
    if (!enabled || !holderId) return;
    const state = redoRaw();
    applyState(state);
  }, [enabled, holderId, redoRaw, applyState]);

  const clearAll = useCallback(() => {
    clear();
    initialCaptured.current = false;
    pendingMutation.current = false;
  }, [clear]);

  return {
    canUndo: enabled && !!holderId && canUndo,
    canRedo: enabled && !!holderId && canRedo,
    undo,
    redo,
    captureSnapshot,
    clear: clearAll,
  };
}
